import { useState, useEffect, useCallback } from 'react';
import type { EntitlementsMap, EntitlementKey, Entitlement } from '@/types/entitlements';
import { ENTITLEMENT_DEFINITIONS, buildEmptyEntitlementsMap, buildEntitlementsMap } from '@/types/entitlements';
import type {
  CustomAppRequestResponse,
  CustomAppPlatform,
  CustomAppRequestPayload,
  CustomAppColors,
  CustomAppLegalData,
  CustomAppSocialLink,
} from '@/types/customApps';
import {
  CUSTOM_APP_PLATFORM_META,
  CUSTOM_APP_STATUS_META,
  ALL_CUSTOM_APP_PLATFORMS,
  DEFAULT_CUSTOM_APP_COLORS,
} from '@/types/customApps';
import { getMyEntitlements } from '@/services/entitlements';
import { createCustomAppRequest, getCustomAppRequests, canCreateNewRequest } from '@/services/customAppRequests';
import { adminEntitlementsData } from '@/mocks/adminEntitlements';
import { SwalAlert } from '@/hooks/useSweetAlert';

// ── TABS ──

type PremiumTab = 'features' | 'custom-app';

// ── COMPONENT ──

interface Props {
  radioId: string;
  radioName: string;
}

export default function EntitlementsTab({ radioId, radioName }: Props) {
  const [entitlements, setEntitlements] = useState<EntitlementsMap>(buildEmptyEntitlementsMap());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PremiumTab>('features');
  const [requests, setRequests] = useState<CustomAppRequestResponse[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // ── Load entitlements ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const map = await getMyEntitlements();
        if (!cancelled) {
          setEntitlements(map);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // Fallback to mock
          setEntitlements(buildEntitlementsMap(adminEntitlementsData.entitlements));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [radioId]);

  // ── Load requests ──
  useEffect(() => {
    if (activeTab !== 'custom-app') return;
    let cancelled = false;
    (async () => {
      setRequestsLoading(true);
      try {
        const data = await getCustomAppRequests(radioId);
        if (!cancelled) setRequests(data);
      } catch {
        if (!cancelled) setRequests(adminEntitlementsData.customAppRequests);
      } finally {
        if (!cancelled) setRequestsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, radioId]);

  // ── Stats ──
  const allEntitlements = Object.values(entitlements);
  const enabledCount = allEntitlements.filter((e) => e.enabled).length;
  const disabledCount = allEntitlements.filter((e) => !e.enabled).length;

  return (
    <div className="space-y-6">
      {/* Tab switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('features')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
            activeTab === 'features'
              ? 'bg-foreground-950 text-background-50'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-vip-diamond-line text-sm"></i>
          Funciones Premium
        </button>
        <button
          onClick={() => setActiveTab('custom-app')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
            activeTab === 'custom-app'
              ? 'bg-foreground-950 text-background-50'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-smartphone-line text-sm"></i>
          App Personalizada
        </button>
      </div>

      {/* Features Tab */}
      {activeTab === 'features' && (
        <FeaturesPanel
          entitlements={entitlements}
          loading={loading}
          error={error}
          enabledCount={enabledCount}
          disabledCount={disabledCount}
        />
      )}

      {/* Custom App Tab */}
      {activeTab === 'custom-app' && (
        <CustomAppPanel
          radioId={radioId}
          radioName={radioName}
          hasEntitlement={entitlements.custom_app?.enabled ?? false}
          requests={requests}
          requestsLoading={requestsLoading}
          onRefresh={async () => {
            try {
              const data = await getCustomAppRequests(radioId);
              setRequests(data);
            } catch {
              // keep current
            }
          }}
        />
      )}
    </div>
  );
}

// ═══════════════ FEATURES PANEL ═══════════════

function FeaturesPanel({
  entitlements,
  loading,
  error,
  enabledCount,
  disabledCount,
}: {
  entitlements: EntitlementsMap;
  loading: boolean;
  error: string | null;
  enabledCount: number;
  disabledCount: number;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-20 bg-background-100 rounded-2xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-red-500 text-xl"></i>
        </div>
        <p className="text-foreground-600 text-sm font-semibold mb-1">Error al cargar</p>
        <p className="text-foreground-400 text-xs">{error}</p>
      </div>
    );
  }

  const enabledFeatures = Object.values(entitlements).filter((e) => e.enabled);
  const disabledFeatures = Object.values(entitlements).filter((e) => !e.enabled);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <i className="ri-check-line text-emerald-600 text-sm"></i>
            </div>
            <span className="text-xl font-bold text-foreground-950">{enabledCount}</span>
          </div>
          <p className="text-[11px] text-foreground-400">Funciones activas</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
              <i className="ri-lock-line text-foreground-400 text-sm"></i>
            </div>
            <span className="text-xl font-bold text-foreground-950">{disabledCount}</span>
          </div>
          <p className="text-[11px] text-foreground-400">Funciones bloqueadas</p>
        </div>
      </div>

      {/* Enabled features */}
      {enabledFeatures.length > 0 && (
        <div className="space-y-3">
          <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">
            Incluidas en tu plan
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {enabledFeatures.map((feature) => (
              <FeatureCard key={feature.key} feature={feature} />
            ))}
          </div>
        </div>
      )}

      {/* Disabled features */}
      {disabledFeatures.length > 0 && (
        <div className="space-y-3">
          <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">
            Requieren mejorar tu plan
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {disabledFeatures.map((feature) => (
              <FeatureCard key={feature.key} feature={feature} />
            ))}
          </div>
        </div>
      )}

      {/* Upgrade CTA */}
      {disabledFeatures.length > 0 && (
        <div className="bg-gradient-to-r from-background-100 to-background-50 border border-background-200 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-foreground-800 text-sm font-bold mb-1">
              ¿Querés desbloquear todas las funciones?
            </p>
            <p className="text-foreground-500 text-xs">
              Mejorá tu plan para acceder a {disabledCount} funciones premium adicionales.
            </p>
          </div>
          <a
            href="/pricing"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-arrow-up-circle-line"></i>
            Ver planes
          </a>
        </div>
      )}
    </div>
  );
}

function FeatureCard({ feature }: { feature: Entitlement }) {
  return (
    <div
      className={`rounded-2xl border p-4 transition-colors ${
        feature.enabled
          ? 'bg-background-50 border-background-200'
          : 'bg-background-100/50 border-background-200/60 opacity-75'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            feature.enabled ? 'bg-emerald-100' : 'bg-foreground-100'
          }`}
        >
          <i
            className={`${feature.icon} text-lg ${
              feature.enabled ? 'text-emerald-600' : 'text-foreground-400'
            }`}
          ></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-foreground-900 text-sm font-bold">{feature.label}</p>
            {!feature.enabled && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-foreground-100 text-foreground-400 text-[9px] font-semibold">
                <i className="ri-lock-line text-[8px]"></i>
                Bloqueado
              </span>
            )}
          </div>
          <p className="text-foreground-500 text-[11px] mt-1 leading-relaxed">
            {feature.description}
          </p>
          {feature.enabled && feature.limit !== null && feature.limitUnit && (
            <p className="text-foreground-400 text-[10px] mt-2 flex items-center gap-1">
              <i className="ri-stack-line text-[10px]"></i>
              {feature.limit === 0
                ? 'Ilimitado'
                : `Hasta ${feature.limit} ${feature.limitUnit}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════ CUSTOM APP PANEL ═══════════════

function CustomAppPanel({
  radioId,
  radioName,
  hasEntitlement,
  requests,
  requestsLoading,
  onRefresh,
}: {
  radioId: string;
  radioName: string;
  hasEntitlement: boolean;
  requests: CustomAppRequestResponse[];
  requestsLoading: boolean;
  onRefresh: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { allowed, reason } = canCreateNewRequest(requests);

  // ── Form state ──
  const [appName, setAppName] = useState(radioName);
  const [selectedPlatforms, setSelectedPlatforms] = useState<CustomAppPlatform[]>([]);
  const [colors, setColors] = useState<CustomAppColors>(DEFAULT_CUSTOM_APP_COLORS);
  const [legalData, setLegalData] = useState<CustomAppLegalData>({
    companyName: '',
    copyright: `© ${new Date().getFullYear()} ${radioName}`,
    privacyPolicyUrl: '',
    termsUrl: '',
    supportEmail: '',
  });
  const [socialLinks, setSocialLinks] = useState<CustomAppSocialLink[]>([]);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const togglePlatform = (p: CustomAppPlatform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  const addSocialLink = () => {
    setSocialLinks((prev) => [...prev, { platform: '', url: '' }]);
  };

  const removeSocialLink = (idx: number) => {
    setSocialLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSocialLink = (idx: number, field: 'platform' | 'url', value: string) => {
    setSocialLinks((prev) => prev.map((link, i) => (i === idx ? { ...link, [field]: value } : link)));
  };

  const handleSubmit = async () => {
    setFormError(null);

    if (!appName.trim()) {
      setFormError('El nombre de la app es obligatorio.');
      return;
    }

    if (selectedPlatforms.length === 0) {
      setFormError('Seleccioná al menos una plataforma.');
      return;
    }

    setSubmitting(true);

    try {
      const payload: CustomAppRequestPayload = {
        app_name: appName.trim(),
        platforms: selectedPlatforms,
        default_radio_id: radioId,
        logo_asset_id: null,
        icon_asset_id: null,
        splash_asset_id: null,
        colors,
        legal_data: legalData.companyName ? legalData : null,
        social_links: socialLinks.filter((l) => l.platform && l.url),
        notes: notes.trim() || null,
      };

      await createCustomAppRequest(radioId, payload);
      await SwalAlert.success('Solicitud enviada', 'Tu solicitud de app personalizada fue registrada. El equipo la revisará y te notificará.');
      setShowForm(false);
      setSelectedPlatforms([]);
      setNotes('');
      onRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al enviar la solicitud.';
      setFormError(message);
      SwalAlert.error('Error', message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* No entitlement warning */}
      {!hasEntitlement && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-amber-700 text-lg"></i>
            </div>
            <div>
              <p className="text-amber-800 text-sm font-bold mb-1">App Personalizada no disponible</p>
              <p className="text-amber-700 text-xs leading-relaxed mb-3">
                Tu plan actual no incluye la función de App Personalizada. Mejorá a un plan que incluya esta funcionalidad para poder solicitarla.
              </p>
              <a
                href="/pricing"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 text-background-50 text-xs font-bold hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-up-circle-line"></i>
                Ver planes
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Existing requests */}
      {!requestsLoading && requests.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">
              Solicitudes existentes
            </p>
            <button
              onClick={onRefresh}
              className="flex items-center gap-1 text-foreground-400 text-[10px] hover:text-foreground-600 cursor-pointer transition-colors"
            >
              <i className="ri-refresh-line text-xs"></i>
              Actualizar
            </button>
          </div>
          <div className="space-y-3">
            {requests.map((req) => {
              const statusMeta = CUSTOM_APP_STATUS_META[req.status];
              return (
                <div key={req.id} className="bg-background-50 border border-background-200 rounded-2xl p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div>
                      <h4 className="text-foreground-950 text-sm font-bold">{req.app_name}</h4>
                      <p className="text-foreground-400 text-[10px] mt-0.5">
                        Solicitada el {new Date(req.requested_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold ${statusMeta.bgClass} ${statusMeta.colorClass} border border-current/20 shrink-0`}>
                      <i className={`${statusMeta.icon} text-[10px]`}></i>
                      {statusMeta.label}
                    </span>
                  </div>

                  {/* Platforms */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {req.platforms.map((p) => {
                      const meta = CUSTOM_APP_PLATFORM_META[p];
                      return (
                        <span key={p} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold">
                          <i className={`${meta.icon} text-[10px]`}></i>
                          {meta.label}
                        </span>
                      );
                    })}
                  </div>

                  {/* Status-specific info */}
                  {req.reviewer_notes && (
                    <div className={`rounded-xl p-3 text-xs ${
                      req.status === 'REJECTED'
                        ? 'bg-red-50 text-red-700'
                        : req.status === 'APPROVED'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-sky-50 text-sky-700'
                    }`}>
                      <p className="font-semibold mb-0.5">
                        {req.status === 'REJECTED' ? 'Motivo:' : req.status === 'APPROVED' ? 'Nota del equipo:' : 'Estado:'}
                      </p>
                      <p className="leading-relaxed">{req.reviewer_notes}</p>
                    </div>
                  )}

                  {/* Production URLs */}
                  {req.production_urls && Object.keys(req.production_urls).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-100">
                      <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                        Enlaces de descarga
                      </p>
                      <div className="space-y-1.5">
                        {Object.entries(req.production_urls).map(([platform, url]) => (
                          url && (
                            <div key={platform} className="flex items-center gap-2 text-xs">
                              <i className={`${CUSTOM_APP_PLATFORM_META[platform as CustomAppPlatform]?.icon || 'ri-link'} text-foreground-400`}></i>
                              <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 truncate">
                                {url}
                              </a>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New request button */}
      {hasEntitlement && (
        <>
          {!showForm ? (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-smartphone-line text-foreground-400 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-sm mb-1">
                {requests.length === 0 ? 'Solicitá tu App Personalizada' : 'Nueva solicitud de App'}
              </h3>
              <p className="text-foreground-500 text-xs mb-4 max-w-md mx-auto leading-relaxed">
                {requests.length === 0
                  ? 'Obtené una app móvil y TV con el branding de tu radio. Disponible para Android, iOS, Android TV, Fire TV, Apple TV, Roku, Samsung TV y LG TV.'
                  : '¿Necesitás otra app o plataformas adicionales? Creá una nueva solicitud.'}
              </p>
              {!allowed ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                  <i className="ri-time-line"></i>
                  {reason}
                </div>
              ) : (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Solicitar App Personalizada
                </button>
              )}
            </div>
          ) : (
            <RequestForm
              appName={appName}
              onAppNameChange={setAppName}
              selectedPlatforms={selectedPlatforms}
              onTogglePlatform={togglePlatform}
              colors={colors}
              onColorsChange={setColors}
              legalData={legalData}
              onLegalDataChange={setLegalData}
              socialLinks={socialLinks}
              onAddSocialLink={addSocialLink}
              onRemoveSocialLink={removeSocialLink}
              onUpdateSocialLink={updateSocialLink}
              notes={notes}
              onNotesChange={setNotes}
              formError={formError}
              submitting={submitting}
              onSubmit={handleSubmit}
              onCancel={() => { setShowForm(false); setFormError(null); }}
            />
          )}
        </>
      )}

      {/* Loading */}
      {requestsLoading && (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-32 bg-background-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════ REQUEST FORM ═══════════════

function RequestForm({
  appName, onAppNameChange,
  selectedPlatforms, onTogglePlatform,
  colors, onColorsChange,
  legalData, onLegalDataChange,
  socialLinks, onAddSocialLink, onRemoveSocialLink, onUpdateSocialLink,
  notes, onNotesChange,
  formError, submitting, onSubmit, onCancel,
}: {
  appName: string;
  onAppNameChange: (v: string) => void;
  selectedPlatforms: CustomAppPlatform[];
  onTogglePlatform: (p: CustomAppPlatform) => void;
  colors: CustomAppColors;
  onColorsChange: (c: CustomAppColors) => void;
  legalData: CustomAppLegalData;
  onLegalDataChange: (d: CustomAppLegalData) => void;
  socialLinks: CustomAppSocialLink[];
  onAddSocialLink: () => void;
  onRemoveSocialLink: (idx: number) => void;
  onUpdateSocialLink: (idx: number, field: 'platform' | 'url', value: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  formError: string | null;
  submitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const mobilePlatforms = ALL_CUSTOM_APP_PLATFORMS.filter((p) => CUSTOM_APP_PLATFORM_META[p].category === 'mobile');
  const tvPlatforms = ALL_CUSTOM_APP_PLATFORMS.filter((p) => CUSTOM_APP_PLATFORM_META[p].category === 'tv');

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-5 md:px-6 py-4 border-b border-background-100 flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground-950">Nueva solicitud de App Personalizada</h3>
        <button
          onClick={onCancel}
          className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
        >
          <i className="ri-close-line"></i>
        </button>
      </div>

      <div className="p-5 md:p-6 space-y-6">
        {/* Error */}
        {formError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs">
            <i className="ri-error-warning-line mr-1.5"></i>
            {formError}
          </div>
        )}

        {/* App name */}
        <div>
          <label className="block text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Nombre de la App
          </label>
          <input
            type="text"
            value={appName}
            onChange={(e) => onAppNameChange(e.target.value)}
            className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            placeholder="Nombre de la app"
          />
        </div>

        {/* Platforms */}
        <div>
          <label className="block text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Plataformas
          </label>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-foreground-400 mb-2">Mobile</p>
              <div className="flex flex-wrap gap-2">
                {mobilePlatforms.map((p) => {
                  const meta = CUSTOM_APP_PLATFORM_META[p];
                  const selected = selectedPlatforms.includes(p);
                  return (
                    <button
                      key={p}
                      onClick={() => onTogglePlatform(p)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                        selected
                          ? 'bg-foreground-950 text-background-50 border-foreground-950'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                      }`}
                    >
                      <i className={`${meta.icon} text-sm`}></i>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-foreground-400 mb-2">TV & Streaming</p>
              <div className="flex flex-wrap gap-2">
                {tvPlatforms.map((p) => {
                  const meta = CUSTOM_APP_PLATFORM_META[p];
                  const selected = selectedPlatforms.includes(p);
                  return (
                    <button
                      key={p}
                      onClick={() => onTogglePlatform(p)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                        selected
                          ? 'bg-foreground-950 text-background-50 border-foreground-950'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                      }`}
                    >
                      <i className={`${meta.icon} text-sm`}></i>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div>
          <label className="block text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Colores
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(colors).map(([key, value]) => (
              <div key={key}>
                <p className="text-[9px] text-foreground-400 mb-1 capitalize">{key}</p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg border border-background-200 overflow-hidden shrink-0">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => onColorsChange({ ...colors, [key]: e.target.value })}
                      className="w-8 h-8 -m-0.5 cursor-pointer"
                    />
                  </div>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => onColorsChange({ ...colors, [key]: e.target.value })}
                    className="flex-1 px-2 py-1 bg-background-100 border border-background-200 rounded-md text-[10px] font-mono text-foreground-600 outline-none"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legal data */}
        <div>
          <label className="block text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Datos Legales
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[9px] text-foreground-400 mb-1">Nombre de la empresa</p>
              <input
                type="text"
                value={legalData.companyName}
                onChange={(e) => onLegalDataChange({ ...legalData, companyName: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                placeholder="Radio Nueva Actitud S.A."
              />
            </div>
            <div>
              <p className="text-[9px] text-foreground-400 mb-1">Copyright</p>
              <input
                type="text"
                value={legalData.copyright}
                onChange={(e) => onLegalDataChange({ ...legalData, copyright: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                placeholder="© 2026 Radio Nueva Actitud"
              />
            </div>
            <div>
              <p className="text-[9px] text-foreground-400 mb-1">URL Política de Privacidad</p>
              <input
                type="url"
                value={legalData.privacyPolicyUrl}
                onChange={(e) => onLegalDataChange({ ...legalData, privacyPolicyUrl: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                placeholder="https://..."
              />
            </div>
            <div>
              <p className="text-[9px] text-foreground-400 mb-1">URL Términos de Uso</p>
              <input
                type="url"
                value={legalData.termsUrl}
                onChange={(e) => onLegalDataChange({ ...legalData, termsUrl: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                placeholder="https://..."
              />
            </div>
            <div>
              <p className="text-[9px] text-foreground-400 mb-1">Email de Soporte</p>
              <input
                type="email"
                value={legalData.supportEmail}
                onChange={(e) => onLegalDataChange({ ...legalData, supportEmail: e.target.value })}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                placeholder="soporte@..."
              />
            </div>
          </div>
        </div>

        {/* Social links */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">
              Redes Sociales de la App
            </label>
            <button
              onClick={onAddSocialLink}
              className="flex items-center gap-1 text-[10px] text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
            >
              <i className="ri-add-line text-xs"></i>
              Agregar
            </button>
          </div>
          <div className="space-y-2">
            {socialLinks.map((link, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={link.platform}
                  onChange={(e) => onUpdateSocialLink(idx, 'platform', e.target.value)}
                  placeholder="Facebook"
                  className="w-32 px-2.5 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                />
                <input
                  type="url"
                  value={link.url}
                  onChange={(e) => onUpdateSocialLink(idx, 'url', e.target.value)}
                  placeholder="https://..."
                  className="flex-1 px-2.5 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                />
                <button
                  onClick={() => onRemoveSocialLink(idx)}
                  className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer transition-colors shrink-0"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
              </div>
            ))}
            {socialLinks.length === 0 && (
              <p className="text-foreground-300 text-[10px] py-1">Sin redes sociales agregadas.</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Notas adicionales
          </label>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none h-24"
            placeholder="Funcionalidades específicas, integraciones, diseño..."
            maxLength={500}
          ></textarea>
          <p className="text-[9px] text-foreground-400 mt-1">{notes.length}/500</p>
        </div>

        {/* Warning: NO credentials */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[10px] text-amber-700 leading-relaxed">
          <i className="ri-error-warning-line mr-1"></i>
          <strong>Importante:</strong> No envíes certificados, contraseñas, API keys, cuentas de desarrollador ni credenciales de tiendas. El equipo de RFW se encargará de la configuración técnica.
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 md:px-6 py-4 border-t border-background-100 flex items-center gap-3">
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          Cancelar
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-1.5"></i>
              Enviando...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-1.5"></i>
              Enviar Solicitud
            </>
          )}
        </button>
      </div>
    </div>
  );
}